/**
 * ReportAccessibilityIssueForm — modal form posting to
 * /api/accessibility/report-issue. Public, no auth.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label, FormError, FormHelp } from './ui/label';
import { Loader2, Send, MessageSquare } from 'lucide-react';
import { BACKEND_URL } from '../config';

export default function ReportAccessibilityIssueForm({ lang = 'en' }) {
  const isEL = lang === 'el';
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [pageUrl, setPageUrl] = useState(typeof window !== 'undefined' ? window.location.href : '');
  const [env, setEnv] = useState('');
  const [description, setDescription] = useState('');
  // Honeypot field — must stay empty
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(null); // {id} when submitted

  const T = (en, el) => (isEL ? el : en);

  const reset = () => {
    setName(''); setEmail(''); setEnv(''); setDescription('');
    setErrors({}); setDone(null); setWebsite('');
  };

  const submit = async (e) => {
    e.preventDefault();
    const nextErr = {};
    if (!description || description.trim().length < 10) {
      nextErr.description = T('Please describe the issue (at least 10 characters).', 'Παρακαλούμε περιγράψτε το πρόβλημα (τουλάχιστον 10 χαρακτήρες).');
    }
    if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      nextErr.email = T('Please enter a valid email address.', 'Παρακαλούμε εισάγετε έγκυρη διεύθυνση e-mail.');
    }
    setErrors(nextErr);
    if (Object.keys(nextErr).length) return;

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/accessibility/report-issue`, {
        name: name || null,
        email: email || null,
        page_url: pageUrl || null,
        environment: env || null,
        description,
        website, // honeypot
      });
      setDone({ id: data.id });
      toast.success(T('Report submitted — thank you!', 'Η αναφορά υποβλήθηκε — ευχαριστούμε!'));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg).join(' · ') : (detail || err.message);
      toast.error(T(`Submit failed: ${msg}`, `Αποτυχία υποβολής: ${msg}`));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        className="border-blue-300 text-blue-800 hover:bg-blue-50"
        onClick={() => { reset(); setOpen(true); }}
        data-testid="open-accessibility-report-form"
        aria-label={T('Open the accessibility issue report form', 'Άνοιγμα της φόρμας αναφοράς προβλήματος προσβασιμότητας')}
      >
        <MessageSquare className="h-4 w-4 mr-1" aria-hidden="true" />
        {T('Report an accessibility issue', 'Αναφορά προβλήματος προσβασιμότητας')}
      </Button>

      <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{T('Report an accessibility issue', 'Αναφορά προβλήματος προσβασιμότητας')}</DialogTitle>
            <DialogDescription>
              {T(
                'Tell us what is preventing you from using this site. We respond within 15 days as required by Greek Law 4727/2020. All fields except the description are optional.',
                'Πείτε μας τι σας εμποδίζει να χρησιμοποιήσετε τον ιστότοπο. Απαντάμε εντός 15 ημερών όπως απαιτείται από τον Ν. 4727/2020. Όλα τα πεδία εκτός της περιγραφής είναι προαιρετικά.',
              )}
            </DialogDescription>
          </DialogHeader>

          {done ? (
            <div className="space-y-3 py-4" role="status" aria-live="polite">
              <p className="text-emerald-800 font-semibold">
                {T('Thank you — your report has been received.', 'Ευχαριστούμε — η αναφορά σας ελήφθη.')}
              </p>
              <p className="text-sm text-slate-600">
                {T('Reference ID:', 'Αρ. αναφοράς:')}{' '}
                <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">{done.id}</code>
              </p>
              <p className="text-sm text-slate-600">
                {T('We will review and respond by email within 15 days. Please keep the reference ID for your records.',
                   'Θα ελέγξουμε την αναφορά και θα απαντήσουμε με e-mail εντός 15 ημερών. Παρακαλούμε κρατήστε τον αριθμό αναφοράς για τα αρχεία σας.')}
              </p>
              <DialogFooter>
                <Button onClick={() => setOpen(false)} data-testid="a11y-report-close">
                  {T('Close', 'Κλείσιμο')}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" aria-label={T('Accessibility issue report form', 'Φόρμα αναφοράς προβλήματος προσβασιμότητας')}>
              {/* Honeypot — visually + AT hidden */}
              <div className="absolute -left-[9999px]" aria-hidden="true">
                <label htmlFor="a11y-website-hp">Website</label>
                <input
                  id="a11y-website-hp"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="a11y-name" optionalHint>{T('Your name', 'Το ονοματεπώνυμό σας')}</Label>
                  <Input
                    id="a11y-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="a11y-report-name"
                    autoComplete="name"
                  />
                </div>
                <div>
                  <Label htmlFor="a11y-email" optionalHint>{T('E-mail (so we can reply)', 'E-mail (για να σας απαντήσουμε)')}</Label>
                  <Input
                    id="a11y-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={!!errors.email}
                    aria-describedby={errors.email ? 'a11y-email-err' : undefined}
                    autoComplete="email"
                    data-testid="a11y-report-email"
                  />
                  <FormError id="a11y-email-err">{errors.email}</FormError>
                </div>
              </div>

              <div>
                <Label htmlFor="a11y-page" optionalHint>{T('Page URL where the issue occurred', 'Διεύθυνση URL της σελίδας που έχει το πρόβλημα')}</Label>
                <Input
                  id="a11y-page"
                  value={pageUrl}
                  onChange={(e) => setPageUrl(e.target.value)}
                  data-testid="a11y-report-page-url"
                />
              </div>

              <div>
                <Label htmlFor="a11y-env" optionalHint>{T('Browser / OS / assistive technology', 'Φυλλομετρητής / Λειτουργικό / Υποστηρικτική τεχνολογία')}</Label>
                <Input
                  id="a11y-env"
                  placeholder={T('e.g. Firefox 142 on Windows 11 with NVDA 2026.1', 'π.χ. Firefox 142 σε Windows 11 με NVDA 2026.1')}
                  value={env}
                  onChange={(e) => setEnv(e.target.value)}
                  data-testid="a11y-report-env"
                />
                <FormHelp id="a11y-env-help">
                  {T(
                    'Knowing your set-up helps us reproduce the issue.',
                    'Γνωρίζοντας τη ρύθμισή σας μας βοηθάει να αναπαράγουμε το πρόβλημα.',
                  )}
                </FormHelp>
              </div>

              <div>
                <Label htmlFor="a11y-desc" required>{T('Description of the issue', 'Περιγραφή του προβλήματος')}</Label>
                <Textarea
                  id="a11y-desc"
                  rows={6}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  error={!!errors.description}
                  aria-describedby={errors.description ? 'a11y-desc-err' : 'a11y-desc-help'}
                  data-testid="a11y-report-description"
                  placeholder={T(
                    'What were you trying to do? What did you expect to happen? What actually happened?',
                    'Τι προσπαθούσατε να κάνετε; Τι περιμένατε να συμβεί; Τι έγινε στην πραγματικότητα;',
                  )}
                />
                {errors.description
                  ? <FormError id="a11y-desc-err">{errors.description}</FormError>
                  : (
                    <FormHelp id="a11y-desc-help">
                      {T(
                        'At least 10 characters. Please include the steps you took if possible.',
                        'Τουλάχιστον 10 χαρακτήρες. Παρακαλούμε συμπεριλάβετε τα βήματα που ακολουθήσατε αν είναι δυνατόν.',
                      )}
                    </FormHelp>
                  )}
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  disabled={submitting}
                  data-testid="a11y-report-cancel"
                >
                  {T('Cancel', 'Άκυρο')}
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="a11y-report-submit"
                >
                  {submitting
                    ? <Loader2 className="h-4 w-4 animate-spin mr-2" aria-hidden="true" />
                    : <Send className="h-4 w-4 mr-2" aria-hidden="true" />}
                  {T('Submit report', 'Υποβολή αναφοράς')}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
